import React, { useState, useMemo, useEffect, useCallback } from 'react';
import {
  Box,
  Stepper,
  Step,
  StepLabel,
  Button,
  Typography,
  Paper,
  Grid,
  Alert,
  CircularProgress,
  Divider,
  Chip,
  FormControlLabel,
  Switch,
  TextField,
  IconButton,
  Tooltip,
  alpha,
} from '@mui/material';
import { Toll as CreditIcon, Add as AddIcon, ContentCopy as TemplatesIcon, ChevronLeft as ChevronLeftIcon, ChevronRight as ChevronRightIcon, HelpOutline as HelpIcon } from '@mui/icons-material';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { boardsApi, productsApi, templatesApi, type PaymentProduct, type PaymentPrice } from '../../api';
import type { CreateBoardInput, PayoutSchemeConfig, BoardTemplate, Sport, CellMode, CellShape, TemplateValidationResult, ExternalGame, TeamMapping } from '../../types';
import { SPORTS, DEFAULT_PAYOUT_CONFIG, getBoxNeighborCount, getTotalCells } from '../../types';
import { PayoutConfigEditor, BoardSettingsForm, CreditBalance, CheckoutModal, CreditsPurchaseModal, TierCreditBadge, GameSelector, ConfirmDialog, generateSlug } from '../../components';
import { getDefaultDollarPayouts } from '../../utils/payoutCalculation';
import { ThemeSelector } from '../../components/theme';
import type { BoardBasicInfo } from '../../components';
import { useCredits, useThemes, useTierConfig } from '../../hooks';
import { useDocumentTitle } from '../../hooks/core';
import { sanitizeHtml } from '@katapult/ui';
import { CreateBoardEducationModal } from '../../components/education';
import { hasSeenEducationModal } from '../../lib/educationModals';

const steps = ['Basic Info', 'Payout Configuration', 'Review & Create'];

export const CreateBoardPage: React.FC = () => {
  const navigate = useNavigate();

  useDocumentTitle('Create Board');

  const [activeStep, setActiveStep] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Credits from context
  const { credits, isLoading: creditsLoading, refreshCredits } = useCredits();

  // Tier config for credit costs
  const { getCreditCost, getDisplayName } = useTierConfig();

  const hasCredits = credits && credits.availableCredits > 0;

  // Credit purchase state
  const [products, setProducts] = useState<PaymentProduct[]>([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<PaymentProduct | null>(null);
  const [selectedPrice, setSelectedPrice] = useState<PaymentPrice | null>(null);

  // In-context credits purchase modal state (for insufficient credits on premium tiers)
  const [creditsPurchaseOpen, setCreditsPurchaseOpen] = useState(false);

  // Education modal state
  const [educationModalOpen, setEducationModalOpen] = useState(() => !hasSeenEducationModal('create_board'));

  // Template state
  const [templates, setTemplates] = useState<BoardTemplate[]>([]);
  const [templatesLoading, setTemplatesLoading] = useState(true);
  const [selectedTemplate, setSelectedTemplate] = useState<BoardTemplate | null>(null);
  const [applyAssignments, setApplyAssignments] = useState(true);
  const [templateFilter, setTemplateFilter] = useState('');
  const [templatePage, setTemplatePage] = useState(0);
  const TEMPLATES_PER_PAGE = 3;
  const [templateValidation, setTemplateValidation] = useState<TemplateValidationResult | null>(null);
  const [validationLoading, setValidationLoading] = useState(false);

  // Live sports state
  const [selectedExternalGameId, setSelectedExternalGameId] = useState<string | undefined>(undefined);
  const [pendingGameSelect, setPendingGameSelect] = useState<{ game: ExternalGame; teamMappings: TeamMapping[] } | null>(null);

  // Theme state
  const [selectedThemeId, setSelectedThemeId] = useState<string | null>(null);
  const { themes } = useThemes();

  // Set default theme to Classic when themes load
  useEffect(() => {
    if (themes.length > 0 && selectedThemeId === null) {
      const classicTheme = themes.find(t => t.lookupKey === 'classic');
      if (classicTheme) {
        setSelectedThemeId(classicTheme.id);
      }
    }
  }, [themes, selectedThemeId]);

  // Form state using BoardBasicInfo
  const [basicInfo, setBasicInfo] = useState<BoardBasicInfo>({
    name: '',
    slug: '',
    description: '',
    sport: 'football',
    homeTeam: '',
    awayTeam: '',
    homeColor: '#1976d2',
    awayColor: '#d32f2f',
    gameDate: '',
    pricePerSquare: 10,
    quarters: 4,
    numberMode: 'single',
  });

  // Check if user has enough credits for the selected tier (calculated from basicInfo.numberMode)
  const requiredCredits = getCreditCost(basicInfo.numberMode);
  const hasEnoughCreditsForTier = credits && credits.availableCredits >= requiredCredits;

  const [profitPercent, setProfitPercent] = useState(10);
  // Initialize with dollar amounts based on default price ($10) and profit (10%)
  // totalPot = $1000, payoutPot = $900, default quarters = 4
  const [payoutConfig, setPayoutConfig] = useState<PayoutSchemeConfig>(() => {
    const defaultDollars = getDefaultDollarPayouts(4, 10, 10);
    return {
      ...DEFAULT_PAYOUT_CONFIG,
      totalPot: 1000,
      quarterPayouts: defaultDollars,
    };
  });


  // Load templates on mount
  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        setTemplatesLoading(true);
        const data = await templatesApi.list();
        setTemplates(data);
      } catch (err) {
        console.error('Failed to load templates:', err);
      } finally {
        setTemplatesLoading(false);
      }
    };
    fetchTemplates();
  }, []);

  // Filtered templates based on search
  const filteredTemplates = useMemo(() => {
    if (!templateFilter.trim()) return templates;
    const query = templateFilter.toLowerCase().trim();
    return templates.filter(t =>
      t.name.toLowerCase().includes(query) ||
      (t.description && t.description.toLowerCase().includes(query)) ||
      t.sport.toLowerCase().includes(query)
    );
  }, [templates, templateFilter]);

  // Paginated templates (1 row = 3 templates)
  const paginatedTemplates = useMemo(() => {
    const start = templatePage * TEMPLATES_PER_PAGE;
    return filteredTemplates.slice(start, start + TEMPLATES_PER_PAGE);
  }, [filteredTemplates, templatePage]);

  const totalTemplatePages = Math.ceil(filteredTemplates.length / TEMPLATES_PER_PAGE);

  // Reset page when filter changes
  const handleTemplateFilterChange = (value: string) => {
    setTemplateFilter(value);
    setTemplatePage(0);
  };

  // Load credit packs when user has no credits
  useEffect(() => {
    const fetchProducts = async () => {
      if (!creditsLoading && !hasCredits) {
        try {
          setProductsLoading(true);
          const creditPacks = await productsApi.listCreditPacks();
          setProducts(creditPacks);
        } catch (err) {
          console.error('Failed to load credit packs:', err);
        } finally {
          setProductsLoading(false);
        }
      }
    };
    fetchProducts();
  }, [creditsLoading, hasCredits]);

  // Handle basic info changes - update payout config when quarters or price changes
  const handleBasicInfoChange = (newInfo: BoardBasicInfo) => {
    const prevQuarters = basicInfo.quarters;
    const prevPrice = basicInfo.pricePerSquare;
    const prevCellMode = basicInfo.cellMode || 'standard';
    const newCellMode = newInfo.cellMode || 'standard';
    const newTotalCells = getTotalCells(newCellMode);
    const prevTotalCells = getTotalCells(prevCellMode);
    setBasicInfo(newInfo);

    // If quarters or cell mode changed, reset to default dollar payouts for new structure
    if (newInfo.quarters !== prevQuarters || newCellMode !== prevCellMode) {
      const dollarPayouts = getDefaultDollarPayouts(newInfo.quarters, newInfo.pricePerSquare, profitPercent, newCellMode);
      setPayoutConfig(prev => ({
        ...prev,
        totalPot: newInfo.pricePerSquare * newTotalCells,
        quarterPayouts: dollarPayouts,
        overtimeConfig: undefined, // Reset OT when sport changes
        // Disable neighbor payouts when changing to non-standard cell mode
        ...(newCellMode !== 'standard' && prev.boxNeighborPayouts?.enabled
          ? { boxNeighborPayouts: { ...prev.boxNeighborPayouts, enabled: false } }
          : {}),
        ...(newCellMode !== 'standard' && prev.neighborPayouts?.enabled
          ? { neighborPayouts: { ...prev.neighborPayouts, enabled: false } }
          : {}),
      }));
    }
    // If only price changed, scale existing dollar amounts proportionally
    else if (newInfo.pricePerSquare !== prevPrice) {
      const oldPayoutPot = prevPrice * prevTotalCells * (1 - profitPercent / 100);
      const newPayoutPot = newInfo.pricePerSquare * newTotalCells * (1 - profitPercent / 100);
      const scaleFactor = oldPayoutPot > 0 ? newPayoutPot / oldPayoutPot : 1;

      setPayoutConfig(prev => {
        const scaledPayouts: Record<string, number> = {};
        for (const [key, amount] of Object.entries(prev.quarterPayouts)) {
          scaledPayouts[key] = Math.round(amount * scaleFactor * 100) / 100;
        }
        return {
          ...prev,
          totalPot: newInfo.pricePerSquare * newTotalCells,
          quarterPayouts: scaledPayouts,
        };
      });
    }
  };

  // Apply a game selection to the board form
  const applyGameSelection = (game: ExternalGame, teamMappings: TeamMapping[]) => {
    const homeMapping = teamMappings.find(t => t.externalTeamId === game.homeTeamId);
    const awayMapping = teamMappings.find(t => t.externalTeamId === game.awayTeamId);

    const sportMap: Record<string, Sport> = {
      nfl: 'football',
      ncaaf: 'football',
      nba: 'basketball',
      ncaab: 'basketball',
      wnba: 'basketball',
      soccer: 'soccer',
      hockey: 'hockey',
    };

    const sport = sportMap[game.sportKey] || 'football';
    const quarters = game.sportKey === 'hockey' ? 3 : game.sportKey === 'soccer' ? 2 : 4;

    // Convert ISO date to datetime-local format (YYYY-MM-DDTHH:MM)
    const gameDateTime = new Date(game.gameDate);
    const localDateStr = gameDateTime.getFullYear() + '-' +
      String(gameDateTime.getMonth() + 1).padStart(2, '0') + '-' +
      String(gameDateTime.getDate()).padStart(2, '0') + 'T' +
      String(gameDateTime.getHours()).padStart(2, '0') + ':' +
      String(gameDateTime.getMinutes()).padStart(2, '0');

    // Build a suggested board name: "Away @ Home - Mar 18"
    const dateStr = gameDateTime.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    const homeAbbr = homeMapping?.abbreviation || game.homeTeam;
    const awayAbbr = awayMapping?.abbreviation || game.awayTeam;
    const suggestedName = `${awayAbbr} @ ${homeAbbr} - ${dateStr}`;

    // Use suggested name if name is empty or was auto-generated from a previous game selection
    const currentSlug = generateSlug(basicInfo.name);
    const wasAutoGenerated = !basicInfo.name || basicInfo.slug === currentSlug;
    const newName = wasAutoGenerated ? suggestedName : basicInfo.name;

    setSelectedExternalGameId(game.id);
    handleBasicInfoChange({
      ...basicInfo,
      name: newName,
      slug: wasAutoGenerated ? generateSlug(newName) : basicInfo.slug,
      homeTeam: game.homeTeam,
      awayTeam: game.awayTeam,
      gameDate: localDateStr,
      sport,
      quarters,
      homeColor: homeMapping?.primaryColor || basicInfo.homeColor,
      awayColor: awayMapping?.primaryColor || basicInfo.awayColor,
    });
  };

  // Handle game selection — show confirmation if game starts within 24 hours
  const handleGameSelect = (game: ExternalGame, teamMappings: TeamMapping[]) => {
    const hoursUntilGame = (new Date(game.gameDate).getTime() - Date.now()) / (1000 * 60 * 60);
    if (hoursUntilGame > 0 && hoursUntilGame < 24) {
      setPendingGameSelect({ game, teamMappings });
      return;
    }
    applyGameSelection(game, teamMappings);
  };

  // Handle profit percent changes - scale dollar amounts proportionally
  const handleProfitPercentChange = useCallback((newProfit: number) => {
    const cellMode = basicInfo.cellMode || 'standard';
    const totalCells = getTotalCells(cellMode);
    const oldPayoutPot = basicInfo.pricePerSquare * totalCells * (1 - profitPercent / 100);
    const newPayoutPot = basicInfo.pricePerSquare * totalCells * (1 - newProfit / 100);
    const scaleFactor = oldPayoutPot > 0 ? newPayoutPot / oldPayoutPot : 1;

    setProfitPercent(newProfit);

    setPayoutConfig(prev => {
      const scaledPayouts: Record<string, number> = {};
      for (const [key, amount] of Object.entries(prev.quarterPayouts)) {
        scaledPayouts[key] = Math.round(amount * scaleFactor * 100) / 100;
      }
      return {
        ...prev,
        quarterPayouts: scaledPayouts,
      };
    });
  }, [basicInfo.pricePerSquare, basicInfo.cellMode, profitPercent]);

  // Calculate pot values
  const currentTotalCells = getTotalCells(basicInfo.cellMode || 'standard');
  const totalPot = basicInfo.pricePerSquare * currentTotalCells;
  const profitAmount = totalPot * (profitPercent / 100);
  const availablePot = totalPot - profitAmount;

  // Calculate current total allocated dollars (quarters + wrong-way + +N + +N wrong-way + custom categories + neighbors)
  const currentPayoutTotal = useMemo(() => {
    const quarterTotal = Object.values(payoutConfig.quarterPayouts).reduce((sum, amount) => sum + amount, 0);
    const wrongWayTotal = Object.values(payoutConfig.quarterWrongWayPayouts || {}).reduce((sum, amount) => sum + amount, 0);
    const plusNTotal = Object.values(payoutConfig.quarterPlusNPayouts || {}).reduce((sum, amount) => sum + amount, 0);
    const plusNWrongWayTotal = Object.values(payoutConfig.quarterPlusNWrongWayPayouts || {}).reduce((sum, amount) => sum + amount, 0);
    // Custom categories: if wrongWayEnabled, payout is doubled
    const customTotal = (payoutConfig.customCategories || []).reduce((sum, cat) => {
      const baseAmount = cat.payoutAmount || 0;
      return sum + (cat.wrongWayEnabled ? baseAmount * 2 : baseAmount);
    }, 0);

    // Neighbor payouts calculation
    const neighborConfig = payoutConfig.neighborPayouts;
    const neighborDirections = neighborConfig?.directions || [];
    const neighborTotal = (() => {
      if (!neighborConfig?.enabled) return 0;
      const directionCount = neighborDirections.length || 0;
      // Get quarter count for calculation
      const quarters = basicInfo.quarters;
      const quarterKeys = quarters === 2 ? ['1H', '2H'] : quarters === 3 ? ['P1', 'P2', 'P3'] : ['Q1', 'Q2', 'Q3', 'Q4'];
      if (neighborConfig.usePerQuarterAmounts && neighborConfig.quarterAmounts) {
        return quarterKeys.reduce(
          (sum, key) => sum + (neighborConfig.quarterAmounts?.[key] || 0) * directionCount,
          0
        );
      }
      return (neighborConfig.globalAmount || 0) * directionCount * quarterKeys.length;
    })();

    // Bad numbers payouts calculation
    const badNumbersConfig = payoutConfig.badNumbersConfig;
    const badNumbersTotal = (() => {
      if (!badNumbersConfig?.enabled) return 0;
      // Use badCombinations if available, otherwise fall back to legacy badDigits
      if (badNumbersConfig.badCombinations && badNumbersConfig.badCombinations.length > 0) {
        return badNumbersConfig.badCombinations.length * (badNumbersConfig.payoutAmount || 0);
      }
      // Legacy fallback
      const badDigitCount = badNumbersConfig.badDigits?.length || 0;
      const badSquareCount = badDigitCount * badDigitCount;
      return badSquareCount * (badNumbersConfig.payoutAmount || 0);
    })();

    // Box neighbor payouts calculation
    const boxNeighborTotal = (() => {
      const boxConfig = payoutConfig.boxNeighborPayouts;
      if (!boxConfig?.enabled) return 0;
      const boxNeighborCount = getBoxNeighborCount(boxConfig.boxSize);
      const quarters = basicInfo.quarters;
      const quarterKeys = quarters === 2 ? ['1H', '2H'] : quarters === 3 ? ['P1', 'P2', 'P3'] : ['Q1', 'Q2', 'Q3', 'Q4'];
      if (boxConfig.usePerQuarterAmounts && boxConfig.quarterAmounts) {
        return quarterKeys.reduce(
          (sum, key) => sum + (boxConfig.quarterAmounts?.[key] || 0) * boxNeighborCount,
          0
        );
      }
      return (boxConfig.globalAmount || 0) * boxNeighborCount * quarterKeys.length;
    })();

    return quarterTotal + wrongWayTotal + plusNTotal + plusNWrongWayTotal + customTotal + neighborTotal + badNumbersTotal + boxNeighborTotal;
  }, [payoutConfig.quarterPayouts, payoutConfig.quarterWrongWayPayouts, payoutConfig.quarterPlusNPayouts, payoutConfig.quarterPlusNWrongWayPayouts, payoutConfig.customCategories, payoutConfig.neighborPayouts, payoutConfig.badNumbersConfig, payoutConfig.boxNeighborPayouts, basicInfo.quarters]);

  // Computed validation states (for disabling Next button)
  const isBasicInfoValid = useMemo(() => {
    return (
      basicInfo.name.trim() !== '' &&
      basicInfo.homeTeam.trim() !== '' &&
      basicInfo.awayTeam.trim() !== '' &&
      basicInfo.gameDate !== ''
    );
  }, [basicInfo.name, basicInfo.homeTeam, basicInfo.awayTeam, basicInfo.gameDate]);

  // Check if at least one payout rule is enabled
  const hasAtLeastOneRule = useMemo(() => {
    const rightWayEnabled = payoutConfig.rightWayEnabled ?? true;
    const wrongWayEnabled = payoutConfig.wrongWayEnabled ?? false;
    const plusNEnabled = payoutConfig.plusFiveEnabled ?? false;
    return rightWayEnabled || wrongWayEnabled || plusNEnabled;
  }, [payoutConfig.rightWayEnabled, payoutConfig.wrongWayEnabled, payoutConfig.plusFiveEnabled]);

  const isPayoutConfigValid = useMemo(() => {
    // Must have at least one rule enabled
    if (!hasAtLeastOneRule) {
      return false;
    }
    const allocatedDollars = currentPayoutTotal;
    const remainingDollars = availablePot - allocatedDollars;
    return Math.abs(remainingDollars) <= 0.01;
  }, [currentPayoutTotal, availablePot, hasAtLeastOneRule]);

  // Check if current step is valid (for disabling Next button)
  const isCurrentStepValid = useMemo(() => {
    if (activeStep === 0) return isBasicInfoValid;
    if (activeStep === 1) return isPayoutConfigValid;
    return true;
  }, [activeStep, isBasicInfoValid, isPayoutConfigValid]);

  // Validate payout configuration - dollar amounts must equal payout pot and at least one rule enabled
  const validatePayoutConfig = () => {
    // Check for at least one rule enabled
    if (!hasAtLeastOneRule) {
      setError('At least one payout rule must be enabled (Right Way, Wrong Way, or Plus Rule)');
      return false;
    }

    const allocatedDollars = currentPayoutTotal;
    const remainingDollars = availablePot - allocatedDollars;

    if (Math.abs(remainingDollars) > 0.01) {
      const allocatedPercent = availablePot > 0 ? (allocatedDollars / availablePot) * 100 : 0;
      if (remainingDollars > 0) {
        setError(`Total payouts must equal 100% of payout pot ($${availablePot.toFixed(2)}). Currently: ${allocatedPercent.toFixed(0)}% ($${allocatedDollars.toFixed(2)}) - $${remainingDollars.toFixed(2)} unallocated`);
      } else {
        setError(`Total payouts exceed payout pot by $${Math.abs(remainingDollars).toFixed(2)}. Please reduce amounts.`);
      }
      return false;
    }
    setError(null);
    return true;
  };

  const handleNext = () => {
    if (activeStep === 0 && !validateBasicInfo()) {
      return;
    }
    if (activeStep === 1 && !validatePayoutConfig()) {
      return;
    }
    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const validateBasicInfo = () => {
    if (!basicInfo.name.trim()) {
      setError('Board name is required');
      return false;
    }
    if (!basicInfo.homeTeam.trim()) {
      setError('Team A is required');
      return false;
    }
    if (!basicInfo.awayTeam.trim()) {
      setError('Team B is required');
      return false;
    }
    if (!basicInfo.gameDate) {
      setError('Game date is required');
      return false;
    }
    setError(null);
    return true;
  };

  // Credit purchase helpers
  const getPrimaryPrice = (product: PaymentProduct): PaymentPrice | undefined => {
    return product.prices?.find(p => p.active) || product.prices?.[0];
  };

  const formatPrice = (price: PaymentPrice): string => {
    return `$${(price.amount / 100).toFixed(2)}`;
  };

  const handlePurchase = (product: PaymentProduct, price: PaymentPrice) => {
    setSelectedProduct(product);
    setSelectedPrice(price);
    setCheckoutOpen(true);
  };

  const handleCheckoutClose = () => {
    setCheckoutOpen(false);
    setSelectedProduct(null);
    setSelectedPrice(null);
  };

  const handleCheckoutSuccess = () => {
    handleCheckoutClose();
    refreshCredits();
  };

  // Template selection handler
  const handleSelectTemplate = async (template: BoardTemplate | null) => {
    setSelectedTemplate(template);
    setTemplateValidation(null);

    if (!template) {
      // Reset to defaults when clearing template
      setBasicInfo(prev => ({
        ...prev,
        sport: 'football',
        quarters: 4,
        pricePerSquare: 10,
        numberMode: 'single',
      }));
      setProfitPercent(10);
      const dollarPayouts = getDefaultDollarPayouts(4, 10, 10);
      setPayoutConfig({
        ...DEFAULT_PAYOUT_CONFIG,
        totalPot: 1000,
        quarterPayouts: dollarPayouts,
      });
      return;
    }

    // Apply template settings
    const newQuarters = template.quarters || 4;
    const newPrice = template.pricePerSquare || 10;
    const newProfit = template.payoutConfig?.profitPercent ?? 10;
    const templateCellMode = (template.cellMode || 'standard') as CellMode;
    const templateCellShape = (template.cellShape || '1x1') as CellShape;
    const templateTotalCells = getTotalCells(templateCellMode);

    setBasicInfo(prev => ({
      ...prev,
      sport: (template.sport || 'football') as Sport,
      quarters: newQuarters,
      pricePerSquare: newPrice,
      numberMode: template.numberMode || 'single',
      cellMode: templateCellMode,
      cellShape: templateCellShape,
    }));

    setProfitPercent(newProfit);

    // Apply template payout config
    if (template.payoutConfig) {
      setPayoutConfig({
        ...template.payoutConfig,
        totalPot: newPrice * templateTotalCells,
      });
    } else {
      const dollarPayouts = getDefaultDollarPayouts(newQuarters, newPrice, newProfit, templateCellMode);
      setPayoutConfig({
        ...DEFAULT_PAYOUT_CONFIG,
        totalPot: newPrice * templateTotalCells,
        quarterPayouts: dollarPayouts,
      });
    }

    // Validate template assignments if it has any
    const assignmentCount = template.assignedSquaresCount ?? template.squareAssignments?.length ?? 0;
    if (assignmentCount > 0) {
      try {
        setValidationLoading(true);
        const validation = await templatesApi.validateAssignments(template.id);
        setTemplateValidation(validation);
        // Auto-disable apply assignments if none are valid
        if (validation.validAssignments === 0) {
          setApplyAssignments(false);
        }
      } catch (err) {
        console.error('Failed to validate template assignments:', err);
      } finally {
        setValidationLoading(false);
      }
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setError(null);

    try {
      let board;

      // If a template is selected with apply assignments, use the template API
      const templateAssignmentCount = selectedTemplate?.assignedSquaresCount ?? selectedTemplate?.squareAssignments?.length ?? 0;
      if (selectedTemplate && applyAssignments && templateAssignmentCount > 0) {
        board = await templatesApi.createBoard(selectedTemplate.id, {
          name: basicInfo.name,
          homeTeam: basicInfo.homeTeam,
          awayTeam: basicInfo.awayTeam,
          homeColor: basicInfo.homeColor,
          awayColor: basicInfo.awayColor,
          gameDate: new Date(basicInfo.gameDate).toISOString(),
          slug: basicInfo.slug || undefined,
          description: basicInfo.description || undefined,
          applyAssignments: true,
          themeId: selectedThemeId || undefined,
        });
      } else {
        // Standard board creation
        const input: CreateBoardInput = {
          name: basicInfo.name,
          slug: basicInfo.slug || undefined,
          description: basicInfo.description,
          sport: basicInfo.sport,
          homeTeam: basicInfo.homeTeam,
          awayTeam: basicInfo.awayTeam,
          homeColor: basicInfo.homeColor,
          awayColor: basicInfo.awayColor,
          gameDate: new Date(basicInfo.gameDate).toISOString(),
          pricePerSquare: basicInfo.pricePerSquare,
          quarters: basicInfo.quarters,
          numberMode: basicInfo.numberMode,
          cellMode: basicInfo.cellMode,
          cellShape: basicInfo.cellShape,
          payoutConfig: {
            ...payoutConfig,
            totalPot: basicInfo.pricePerSquare * getTotalCells(basicInfo.cellMode || 'standard'),
            profitPercent,
          },
          themeId: selectedThemeId || undefined,
          externalGameId: selectedExternalGameId || undefined,
        };
        board = await boardsApi.create(input);
      }

      // Refresh credits after successful board creation
      await refreshCredits();
      navigate(`/boards/${board.slug || board.id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create board');
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <Box>
            <GameSelector
              onGameSelect={handleGameSelect}
              selectedGameId={selectedExternalGameId}
            />

            {selectedExternalGameId && (
              <Paper
                elevation={0}
                sx={{
                  mb: 2,
                  p: 2,
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'primary.main',
                  bgcolor: (theme) => alpha(theme.palette.primary.main, 0.04),
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                }}
              >
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    bgcolor: 'primary.main',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <CreditIcon sx={{ color: '#fff', fontSize: 20 }} />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle2" fontWeight={700}>
                    Premium Live Board
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Linked to a live game — scoring starts automatically. Teams and game date are locked.
                  </Typography>
                </Box>
                <Chip
                  label="LIVE"
                  size="small"
                  color="primary"
                  sx={{ fontWeight: 700, letterSpacing: '0.05em' }}
                />
              </Paper>
            )}

            <Typography variant="h6" gutterBottom>
              Board Details
            </Typography>
            <BoardSettingsForm
              values={basicInfo}
              onChange={handleBasicInfoChange}
              mode="create"
              showSlug={true}
              showTeamColors={!selectedThemeId}
              showPricePerSquare={false}
              lockCellMode={!!(selectedTemplate && (selectedTemplate.assignedSquaresCount ?? 0) > 0)}
              isLiveGame={!!selectedExternalGameId}
            />

            <Divider sx={{ my: 3 }} />

            <Typography variant="h6" gutterBottom>
              Board Theme (Optional)
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Select a visual theme to customize your board's appearance.
              {selectedThemeId ? ' The theme colors will be used for team labels.' : ' You can change this later in board settings.'}</Typography>
            <ThemeSelector
              selectedThemeId={selectedThemeId}
              onSelect={setSelectedThemeId}
              showPurchaseButton={false}
            />
          </Box>
        );

      case 1:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Payout Configuration
            </Typography>
            <PayoutConfigEditor
              payoutConfig={payoutConfig}
              onPayoutConfigChange={setPayoutConfig}
              pricePerSquare={basicInfo.pricePerSquare}
              onPricePerSquareChange={(price) => handleBasicInfoChange({ ...basicInfo, pricePerSquare: price })}
              profitPercent={profitPercent}
              onProfitPercentChange={handleProfitPercentChange}
              quarters={basicInfo.quarters}
              totalCells={getTotalCells(basicInfo.cellMode || 'standard')}
              cellMode={basicInfo.cellMode || 'standard'}
            />
          </Box>
        );

      case 2:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Review Your Board
            </Typography>
            <Paper sx={{ p: 3 }}>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: basicInfo.slug ? 6 : 12 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Board Name
                  </Typography>
                  <Typography variant="body1">{basicInfo.name}</Typography>
                </Grid>
                {basicInfo.slug && (
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Custom URL
                    </Typography>
                    <Typography variant="body1">/b/{basicInfo.slug}</Typography>
                  </Grid>
                )}
                {basicInfo.description && (
                  <Grid size={12}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Description
                    </Typography>
                    <Box
                      sx={{ '& a': { color: 'primary.main' } }}
                      dangerouslySetInnerHTML={{ __html: sanitizeHtml(basicInfo.description) }}
                    />
                  </Grid>
                )}
                <Grid size={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Sport
                  </Typography>
                  <Typography variant="body1">
                    {SPORTS.find((s) => s.value === basicInfo.sport)?.label}
                  </Typography>
                </Grid>
                <Grid size={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Price Per Square
                  </Typography>
                  <Typography variant="body1">${basicInfo.pricePerSquare}</Typography>
                </Grid>
                <Grid size={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Scoring Periods
                  </Typography>
                  <Typography variant="body1">
                    {basicInfo.quarters === 2 ? '2 Halves' : basicInfo.quarters === 3 ? '3 Periods' : '4 Quarters'}
                  </Typography>
                </Grid>
                <Grid size={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Number Sets
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body1">
                      {getDisplayName(basicInfo.numberMode)}
                    </Typography>
                    <TierCreditBadge numberMode={basicInfo.numberMode} size="small" />
                  </Box>
                </Grid>
                <Grid size={12}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Matchup
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                      sx={{
                        width: 16,
                        height: 16,
                        borderRadius: '50%',
                        backgroundColor: basicInfo.awayColor,
                      }}
                    />
                    <Typography variant="body1">{basicInfo.awayTeam}</Typography>
                    <Typography variant="body1" color="text.secondary">-</Typography>
                    <Box
                      sx={{
                        width: 16,
                        height: 16,
                        borderRadius: '50%',
                        backgroundColor: basicInfo.homeColor,
                      }}
                    />
                    <Typography variant="body1">{basicInfo.homeTeam}</Typography>
                  </Box>
                </Grid>
                <Grid size={12}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Game Date
                  </Typography>
                  <Typography variant="body1">
                    {new Date(basicInfo.gameDate).toLocaleString()}
                  </Typography>
                </Grid>
                <Grid size={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Total Pot
                  </Typography>
                  <Typography variant="h5" color="primary">
                    ${totalPot.toFixed(2)}
                  </Typography>
                </Grid>
                {profitPercent > 0 && (
                  <Grid size={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Organizer Profit ({profitPercent}%)
                    </Typography>
                    <Typography variant="h5" color="success.main">
                      ${profitAmount.toFixed(2)}
                    </Typography>
                  </Grid>
                )}
                <Grid size={12}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Payout Structure (from ${availablePot.toFixed(2)} available)
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 1 }}>
                    {Object.entries(payoutConfig.quarterPayouts).map(([q, amount]) => {
                      const pct = availablePot > 0 ? (amount / availablePot) * 100 : 0;
                      return (
                        <Typography key={q} variant="body2">
                          {q}: ${amount.toFixed(2)} ({pct.toFixed(0)}%)
                        </Typography>
                      );
                    })}
                  </Box>
                </Grid>
                {payoutConfig.reverseEnabled && (
                  <Grid size={12}>
                    <Typography variant="body2" color="success.main">
                      Reverse Winners enabled
                    </Typography>
                  </Grid>
                )}
                {payoutConfig.plusFiveEnabled && (
                  <Grid size={12}>
                    <Typography variant="body2" color="success.main">
                      +5 Rule enabled
                    </Typography>
                  </Grid>
                )}
                {payoutConfig.overtimeConfig?.enabled && (
                  <Grid size={12}>
                    <Typography variant="body2" color="success.main">
                      OT Split enabled: ${(payoutConfig.overtimeConfig.overtimeAmount ?? 0).toFixed(2)} to Final
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Paper>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="h4" component="h1">
            Create New Board
          </Typography>
          <Tooltip title="Learn more about creating boards">
            <IconButton
              component={RouterLink}
              to="/support/boards#creating-board"
              size="small"
              sx={{ color: 'text.secondary' }}
            >
              <HelpIcon />
            </IconButton>
          </Tooltip>
        </Box>
        <CreditBalance variant="chip" />
      </Box>

      {/* No credits - show purchase section */}
      {!creditsLoading && !hasCredits && (
        <Paper sx={{ p: 3, mb: 3, bgcolor: 'warning.light' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <CreditIcon color="warning" />
            <Typography variant="h6">You need credits to create a board</Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Board creation costs vary based on number set complexity: 1 credit for single sets, 2 credits for half sets, and 4 credits for quarter sets.
          </Typography>
          <Divider sx={{ my: 2 }} />

          {productsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={24} />
            </Box>
          ) : products.length === 0 ? (
            <Alert severity="info" sx={{ mt: 1 }}>
              No credit packs are currently available. Please check back later.
            </Alert>
          ) : (
            <Grid container spacing={2}>
              {products.map((product) => {
                const price = getPrimaryPrice(product);
                const productCredits = product.metadata.boardCredits || 0;
                // Best value = lowest cost per board
                const isBestValue = products.length > 1 && products.every(p => {
                  const pPrice = getPrimaryPrice(p);
                  const pCredits = p.metadata.boardCredits || 1;
                  const pCostPerBoard = (pPrice?.amount || 0) / pCredits;
                  const thisCostPerBoard = (price?.amount || 0) / (productCredits || 1);
                  return thisCostPerBoard <= pCostPerBoard;
                });

                if (!price) return null;

                return (
                  <Grid size={{ xs: 12, sm: 4 }} key={product.id}>
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 2,
                        textAlign: 'center',
                        border: 2,
                        borderColor: isBestValue ? 'primary.main' : 'divider',
                        bgcolor: 'background.paper',
                        position: 'relative',
                      }}
                    >
                      {isBestValue && (
                        <Box
                          sx={{
                            position: 'absolute',
                            top: -10,
                            left: '50%',
                            transform: 'translateX(-50%)',
                            bgcolor: 'primary.main',
                            color: 'white',
                            px: 1.5,
                            py: 0.25,
                            borderRadius: 1,
                            fontSize: '0.7rem',
                            fontWeight: 'bold',
                          }}
                        >
                          BEST VALUE
                        </Box>
                      )}
                      <Typography variant="h5" color="primary">{productCredits}</Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        Credit{productCredits !== 1 ? 's' : ''}
                      </Typography>
                      <Typography variant="h6" sx={{ mb: 1 }}>
                        {formatPrice(price)}
                      </Typography>
                      <Button
                        variant={isBestValue ? 'contained' : 'outlined'}
                        size="small"
                        fullWidth
                        startIcon={<AddIcon />}
                        onClick={() => handlePurchase(product, price)}
                      >
                        Buy
                      </Button>
                    </Paper>
                  </Grid>
                );
              })}
            </Grid>
          )}
        </Paper>
      )}

      {/* Only show template selector and form when user has credits */}
      {hasCredits && (
        <>
      {/* Template Selector */}
      {!templatesLoading && templates.length > 0 && (
        <Paper sx={{ mb: 3, p: 2 }}>
          {/* Header with title and New Template button */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <TemplatesIcon color={selectedTemplate ? 'primary' : 'action'} />
              <Typography fontWeight="medium">
                {selectedTemplate ? `Using Template: ${selectedTemplate.name}` : 'Start from a Template'}
              </Typography>
              {selectedTemplate && (
                <Chip
                  label={selectedTemplate.sport}
                  size="small"
                  variant="outlined"
                />
              )}
            </Box>
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={() => navigate('/profile/templates')}
            >
              New Template
            </Button>
          </Box>

          {/* Filter and pagination controls */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <TextField
              size="small"
              placeholder="Filter templates..."
              value={templateFilter}
              onChange={(e) => handleTemplateFilterChange(e.target.value)}
              sx={{ width: 200 }}
              InputProps={{
                sx: { height: 32 },
              }}
            />
            {totalTemplatePages > 1 && (
              <Box sx={{ display: 'flex', alignItems: 'center', ml: 'auto' }}>
                <IconButton
                  size="small"
                  onClick={() => setTemplatePage(p => Math.max(0, p - 1))}
                  disabled={templatePage === 0}
                >
                  <ChevronLeftIcon />
                </IconButton>
                <Typography variant="body2" color="text.secondary" sx={{ mx: 1 }}>
                  {templatePage + 1} / {totalTemplatePages}
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => setTemplatePage(p => Math.min(totalTemplatePages - 1, p + 1))}
                  disabled={templatePage >= totalTemplatePages - 1}
                >
                  <ChevronRightIcon />
                </IconButton>
              </Box>
            )}
          </Box>

          {/* Template cards */}
          {filteredTemplates.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
              No templates found matching "{templateFilter}"
            </Typography>
          ) : (
            <Grid container spacing={2}>
              {paginatedTemplates.map((template) => {
                const isSelected = selectedTemplate?.id === template.id;
                const totalCells = getTotalCells(template.cellMode || 'standard');
                const rawAssignmentCount = template.assignedSquaresCount ?? template.squareAssignments?.length ?? 0;
                const assignmentCount = Math.min(rawAssignmentCount, totalCells);

                return (
                  <Grid size={{ xs: 12, sm: 6, md: 4 }} key={template.id}>
                    <Paper
                      variant={isSelected ? 'elevation' : 'outlined'}
                      sx={{
                        p: 2,
                        cursor: 'pointer',
                        border: 2,
                        borderColor: isSelected ? 'primary.main' : 'divider',
                        bgcolor: isSelected ? 'primary.light' : 'background.paper',
                        transition: 'all 0.2s',
                        '&:hover': {
                          borderColor: 'primary.main',
                        },
                      }}
                      onClick={() => handleSelectTemplate(isSelected ? null : template)}
                    >
                      <Typography fontWeight="medium" sx={{ color: isSelected ? 'primary.contrastText' : 'text.primary' }}>
                        {template.name}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                        <Chip
                          label={`${totalCells} cells`}
                          size="small"
                          sx={{
                            bgcolor: isSelected ? 'rgba(255,255,255,0.3)' : undefined,
                            color: isSelected ? 'primary.contrastText' : undefined,
                          }}
                        />
                        {assignmentCount > 0 && (
                          <Chip
                            label={`${assignmentCount} assigned`}
                            size="small"
                            sx={{
                              bgcolor: isSelected ? 'rgba(255,255,255,0.3)' : undefined,
                              color: isSelected ? 'primary.contrastText' : undefined,
                            }}
                          />
                        )}
                      </Box>
                      {template.description && (
                        <Typography
                          variant="caption"
                          sx={{
                            display: 'block',
                            mt: 1,
                            color: isSelected ? 'rgba(255,255,255,0.8)' : 'text.secondary',
                          }}
                        >
                          {template.description.length > 60
                            ? `${template.description.substring(0, 60)}...`
                            : template.description}
                        </Typography>
                      )}
                    </Paper>
                  </Grid>
                );
              })}
            </Grid>
          )}

          {/* Apply assignments toggle */}
          {selectedTemplate && (selectedTemplate.assignedSquaresCount ?? selectedTemplate.squareAssignments?.length ?? 0) > 0 && (
            <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
              {validationLoading ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={16} />
                  <Typography variant="body2" color="text.secondary">
                    Validating assignments...
                  </Typography>
                </Box>
              ) : templateValidation && !templateValidation.valid ? (
                <>
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    <Typography variant="body2" fontWeight="medium">
                      Some assignments cannot be applied
                    </Typography>
                    <Typography variant="body2">
                      {templateValidation.validAssignments} of {templateValidation.totalAssignments} assignments will be applied.
                      {templateValidation.missingPlayers && templateValidation.missingPlayers.length > 0 && (
                        <> {templateValidation.missingPlayers.length} player(s) or connection(s) no longer exist.</>
                      )}
                    </Typography>
                  </Alert>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={applyAssignments}
                        onChange={(e) => setApplyAssignments(e.target.checked)}
                        disabled={templateValidation.validAssignments === 0}
                      />
                    }
                    label={templateValidation.validAssignments === 0
                      ? 'No valid assignments to apply'
                      : `Apply ${templateValidation.validAssignments} valid assignment${templateValidation.validAssignments !== 1 ? 's' : ''}`}
                  />
                </>
              ) : (
                <FormControlLabel
                  control={
                    <Switch
                      checked={applyAssignments}
                      onChange={(e) => setApplyAssignments(e.target.checked)}
                    />
                  }
                  label={`Auto-assign ${selectedTemplate.assignedSquaresCount ?? selectedTemplate.squareAssignments?.length ?? 0} squares to offline players`}
                />
              )}
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 4 }}>
                When enabled, squares will be automatically assigned based on the template's player positions.
              </Typography>
            </Box>
          )}
        </Paper>
      )}

      {/* Template CTA when no templates exist */}
      {!templatesLoading && templates.length === 0 && (
        <Alert
          severity="info"
          sx={{ mb: 3, alignItems: 'center' }}
          action={
            <Button
              variant="contained"
              size="small"
              onClick={() => navigate('/profile/templates')}
              sx={{ whiteSpace: 'nowrap', minWidth: 'auto' }}
            >
              Create Template
            </Button>
          }
        >
          <Typography variant="body2">
            <strong>Save time with templates!</strong> Create reusable board templates with pre-configured settings and player assignments.
          </Typography>
        </Alert>
      )}

      <Stepper
        activeStep={activeStep}
        alternativeLabel
        sx={{
          mb: 4,
          '& .MuiStepLabel-label': {
            fontSize: { xs: '0.75rem', sm: '0.875rem' },
          },
        }}
      >
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Insufficient credits warning for the selected tier */}
      {activeStep === steps.length - 1 && hasCredits && !hasEnoughCreditsForTier && (
        <Alert
          severity="warning"
          sx={{ mb: 3 }}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => setCreditsPurchaseOpen(true)}
            >
              Buy Credits
            </Button>
          }
        >
          You need {requiredCredits} credit{requiredCredits !== 1 ? 's' : ''} to create this board, but you only have {credits?.availableCredits || 0}.
          {' '}Purchase more credits or select a different number set option (like "Same for All Periods" for 1 credit).
        </Alert>
      )}

      <Paper sx={{ p: 4 }}>
        {renderStepContent(activeStep)}

        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
          <Button disabled={activeStep === 0} onClick={handleBack} data-testid="create-board-back-button">
            Back
          </Button>
          {activeStep === steps.length - 1 ? (
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={isSubmitting || !hasEnoughCreditsForTier}
              data-testid="create-board-submit-button"
            >
              {isSubmitting ? 'Creating...' : `Create Board (${requiredCredits} Credit${requiredCredits !== 1 ? 's' : ''})`}
            </Button>
          ) : (
            <Button variant="contained" onClick={handleNext} disabled={!isCurrentStepValid} data-testid="create-board-next-button">
              Next
            </Button>
          )}
        </Box>
      </Paper>

        </>
      )}

      {/* Checkout Modal */}
      {selectedProduct && selectedPrice && (
        <CheckoutModal
          open={checkoutOpen}
          onClose={handleCheckoutClose}
          onSuccess={handleCheckoutSuccess}
          product={selectedProduct}
          price={selectedPrice}
        />
      )}

      {/* In-context Credits Purchase Modal */}
      <CreditsPurchaseModal
        open={creditsPurchaseOpen}
        onClose={() => setCreditsPurchaseOpen(false)}
        onSuccess={() => {
          setCreditsPurchaseOpen(false);
          refreshCredits();
        }}
        requiredCredits={requiredCredits}
        contextMessage={`Creating a board with "${getDisplayName(basicInfo.numberMode)}" number sets requires ${requiredCredits} credit${requiredCredits !== 1 ? 's' : ''}.`}
      />

      <CreateBoardEducationModal
        open={educationModalOpen}
        onClose={() => setEducationModalOpen(false)}
      />

      <ConfirmDialog
        open={!!pendingGameSelect}
        title="Game Starting Soon"
        icon="warning"
        confirmLabel="Continue"
        confirmColor="warning"
        onConfirm={() => {
          if (pendingGameSelect) {
            applyGameSelection(pendingGameSelect.game, pendingGameSelect.teamMappings);
          }
          setPendingGameSelect(null);
        }}
        onCancel={() => setPendingGameSelect(null)}
      >
        <Typography variant="body2" sx={{ mb: 1 }}>
          This game starts in less than 24 hours. You'll have limited time to fill all the squares on your board before the game begins.
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Make sure you can get enough players to join before kickoff. Unfilled squares won't participate in payouts.
        </Typography>
      </ConfirmDialog>
    </Box>
  );
};
